/**
 * PCM in, a WAV file out: what `POST /api/voice/stt` takes (16 kHz, mono, 16-bit). Kept
 * free of the DOM so it can be tested; the resampling a browser does better is in stt.ts,
 * and `resampleLinear` is its fallback where there is no OfflineAudioContext.
 */

export const STT_SAMPLE_RATE = 16_000;

/** Joins captured blocks into one buffer. */
export function concatSamples(blocks: Float32Array[]): Float32Array {
  const total = blocks.reduce((sum, block) => sum + block.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  for (const block of blocks) {
    out.set(block, offset);
    offset += block.length;
  }
  return out;
}

/**
 * Linear-interpolation resampling with a box pre-filter when going down, which is enough
 * for speech into whisper. The browser's OfflineAudioContext is preferred where it exists.
 */
export function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate || input.length === 0) return input.slice();
  const ratio = fromRate / toRate;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(length);
  const span = ratio > 1 ? Math.floor(ratio) : 1;
  for (let i = 0; i < length; i++) {
    const position = i * ratio;
    if (span > 1) {
      // Average the input samples this output sample covers (a crude low-pass).
      const start = Math.floor(position);
      let sum = 0;
      let count = 0;
      for (let j = start; j < start + span && j < input.length; j++) {
        sum += input[j]!;
        count++;
      }
      out[i] = count ? sum / count : 0;
    } else {
      const left = Math.floor(position);
      const right = Math.min(left + 1, input.length - 1);
      const t = position - left;
      out[i] = input[left]! * (1 - t) + input[right]! * t;
    }
  }
  return out;
}

/** Float samples in [-1, 1] (clamped) as a mono 16-bit PCM WAV. */
export function encodeWav(samples: Float32Array, sampleRate: number = STT_SAMPLE_RATE): ArrayBuffer {
  const bytesPerSample = 2;
  const dataBytes = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, "data");
  view.setUint32(40, dataBytes, true);
  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const sample = Math.max(-1, Math.min(1, samples[i]!));
    view.setInt16(offset, sample < 0 ? Math.round(sample * 0x8000) : Math.round(sample * 0x7fff), true);
  }
  return buffer;
}

/** Root-mean-square of a block: the loudness the level meter and the silence detector read. */
export function rms(block: Float32Array): number {
  if (block.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < block.length; i++) sum += block[i]! * block[i]!;
  return Math.sqrt(sum / block.length);
}
